import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GroupedPlans, OptionKey, PlanItem, useClickOutside } from '@common-fe/common-fe';

import { DROPDOWN_PANEL_REQUIRED_SPACE } from '../PlanTemplatePickerDropdown.styles';

interface UsePlanTemplatePickerDropdownParams {
  options: GroupedPlans[];
  defaultValues?: OptionKey[];
  onChange: (value: OptionKey[]) => void;
}

export const usePlanTemplatePickerDropdown = ({
  options,
  defaultValues,
  onChange,
}: UsePlanTemplatePickerDropdownParams) => {
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isShown, setIsShown] = useState(false);
  const [openToLeft, setOpenToLeft] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useClickOutside(ref as any, () => {
    if (isShown) {
      setSearchValue('');
      setIsShown(false);
    }
  });

  const handlePick = useCallback(
    (newValue?: string) => (e: React.MouseEvent) => {
      e.preventDefault();
      onChange([newValue]);
      setIsShown(false);
      setSearchValue('');
    },
    [onChange]
  );

  const handleOpen = useCallback(() => {
    setIsShown(true);
  }, []);

  const filteredOptions = useMemo(() => {
    if (!searchValue) return options;

    const lowerSearch = searchValue.toLowerCase();
    return options
      .map((category) => ({
        ...category,
        plans: category.plans?.filter((p) => p.name.toLowerCase().includes(lowerSearch)) || [],
      }))
      .filter((category) => category.plans.length > 0);
  }, [options, searchValue]);

  const selectedTemplateNames = useMemo(() => {
    if (!defaultValues?.length) return '';

    return defaultValues
      .map(
        (key) => options.flatMap((category) => category.plans).find((t: PlanItem) => t.id === key)?.name || ''
      )
      .filter(Boolean)
      .join(', ');
  }, [defaultValues, options]);

  const selectedCategory = useMemo(() => {
    if (!defaultValues?.length) return null;
    for (const category of options) {
      if (category.plans?.some((p: PlanItem) => defaultValues.includes(p.id))) {
        return category.category;
      }
    }
    return null;
  }, [defaultValues, options]);

  useEffect(() => {
    if (isShown && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isShown]);

  useEffect(() => {
    if (isShown && ref.current) {
      const triggerRect = ref.current.getBoundingClientRect();
      const spaceRight = window.innerWidth - triggerRect.right;
      setOpenToLeft(spaceRight < DROPDOWN_PANEL_REQUIRED_SPACE);
    }
  }, [isShown]);

  const isItemSelected = useCallback((id: string) => !!defaultValues?.includes(id), [defaultValues]);

  return {
    ref,
    inputRef,
    isShown,
    setIsShown,
    openToLeft,
    searchValue,
    setSearchValue,
    filteredOptions,
    handlePick,
    handleOpen,
    selectedTemplateNames,
    selectedCategory,
    isItemSelected,
  };
};
